import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { HomeLayout } from "../components/layout/HomeLayout";
import { BeginnerPathPhaseShell } from "../components/beginnerPath/BeginnerPathPhaseShell";
import { ScalePathLibrary } from "../components/scales/path/ScalePathLibrary";
import { ScalePathMissionStudio } from "../components/scales/path/ScalePathMissionStudio";
import { useBeginnerPathEntry } from "../hooks/useBeginnerPathEntry";
import {
  getFirstUnlockedScalePathMission,
  getScalePathMissionById,
  SCALE_PATH_MISSIONS,
} from "../lib/scales/scalePathPlan";
import {
  loadScalePathProgress,
  markScalePathMissionComplete,
  saveScalePathProgress,
} from "../lib/scales/scalePathProgress";
import {
  loadBeginnerPathProgress,
  markBeginnerPhaseComplete,
  saveBeginnerPathProgress,
} from "../lib/beginnerPath/beginnerPathPlan";
import { HOME_THEME as C } from "../lib/homeTheme";
import { BEGINNER_PATH_PAGE_CLASS } from "../lib/beginnerPath/beginnerPathLayout";

export const ScalePathPage: React.FC = () => {
  const navigate = useNavigate();
  const { fromPath, phase } = useBeginnerPathEntry();
  const pathMode = fromPath && !!phase;

  const [activeMissionId, setActiveMissionId] = useState<string | null>(null);
  const [progress, setProgress] = useState(loadScalePathProgress);
  const autoOpenedFromPathRef = useRef(false);

  useEffect(() => {
    if (!pathMode || activeMissionId || autoOpenedFromPathRef.current) return;
    const first = getFirstUnlockedScalePathMission(progress.completedMissionIds);
    if (first) {
      autoOpenedFromPathRef.current = true;
      setActiveMissionId(first.id);
    }
  }, [pathMode, activeMissionId, progress.completedMissionIds]);

  useEffect(() => {
    saveScalePathProgress(progress);
    const allDone = SCALE_PATH_MISSIONS.every((m) =>
      progress.completedMissionIds.includes(m.id)
    );
    if (allDone && pathMode) {
      const bp = loadBeginnerPathProgress();
      if (!bp.completedPhaseIds.includes("scales-learn")) {
        saveBeginnerPathProgress(markBeginnerPhaseComplete(bp, "scales-learn"));
      }
    }
  }, [progress, pathMode]);

  const activeMission = activeMissionId
    ? getScalePathMissionById(activeMissionId)
    : null;

  const handleMissionComplete = useCallback(() => {
    if (!activeMissionId) return;
    setProgress((p) => markScalePathMissionComplete(p, activeMissionId));
    setActiveMissionId(null);
  }, [activeMissionId]);

  const inner = activeMission ? (
    <ScalePathMissionStudio
      mission={activeMission}
      wasCompletedBefore={progress.completedMissionIds.includes(activeMission.id)}
      pathMode={pathMode}
      onBack={() => setActiveMissionId(null)}
      onComplete={handleMissionComplete}
    />
  ) : (
    <ScalePathLibrary
      progress={progress}
      pathMode={pathMode}
      onSelectMission={setActiveMissionId}
    />
  );

  if (pathMode && phase) {
    return (
      <BeginnerPathPhaseShell phase={phase} fullBleed={!!activeMission}>
        {inner}
      </BeginnerPathPhaseShell>
    );
  }

  return (
    <HomeLayout>
      <div className={BEGINNER_PATH_PAGE_CLASS}>
        <button
          type="button"
          onClick={() => navigate("/scales")}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase transition-colors hover:text-[#c4b5fd]"
          style={{ color: C.muted }}
        >
          <ArrowLeft className="h-4 w-4" />
          Hub de escalas
        </button>
        <header className="mt-8 mb-8">
          <p className="text-xs tracking-widest uppercase text-[#c4b5fd]/70">
            Misiones guiadas
          </p>
          <h1 className="mt-3 font-serif text-3xl font-light text-white/90">
            Aprende la escala en POS I
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-white/55">
            Mismo motor que el explorador libre, acotado a la primera posición canónica 3NPS.
          </p>
        </header>
        {inner}
      </div>
    </HomeLayout>
  );
};

export default ScalePathPage;
